<script setup lang="ts">
import { computed } from 'vue'

export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info'

const props = withDefaults(
  defineProps<{
    /** Count or short text; the default slot overrides it. */
    value?: number | string | null
    variant?: BadgeVariant
    pill?: boolean
    /** Render nothing for 0 / empty (legacy counters disappear at 0). */
    hideZero?: boolean
  }>(),
  { value: null, variant: 'primary', pill: true, hideZero: true },
)

const visible = computed(
  () => !props.hideZero || (props.value !== null && props.value !== '' && Number(props.value) !== 0),
)
</script>

<template>
  <span
    v-if="visible || $slots.default"
    class="app-badge"
    :class="[`app-badge--${variant}`, { 'app-badge--pill': pill }]"
    data-kit="badge"
  >
    <slot>{{ value }}</slot>
  </span>
</template>

<style scoped>
.app-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.3rem;
  padding: 0.2rem 0.4rem;
  font-size: 0.786rem;
  font-weight: 600;
  line-height: 1;
  color: #fff;
  border-radius: var(--p-border-radius-md);
  white-space: nowrap;
}
.app-badge--pill {
  border-radius: 10rem;
}
.app-badge--primary {
  background: var(--p-primary-500);
}
.app-badge--secondary {
  background: var(--p-surface-600);
}
.app-badge--success {
  background: var(--p-green-500);
}
.app-badge--danger {
  background: var(--p-red-500);
}
.app-badge--warning {
  background: var(--p-orange-500);
}
.app-badge--info {
  background: var(--p-blue-500);
}
</style>
